import { useState } from 'react';

import config from '@/config';
import useDoubleTapHandle from '@/hooks/useDoubleTapHandle';
import useProtectAction from '@/hooks/useProtectAction';
import DoubleTabLike from '@/shared-components/animations/components/DoubleTabLike';
import { getArtworkArtistDisplayName } from '@/utils/artworkArtists';

import './styles/ArtworkImage.css';

export default function ArtworkImage({
  image,
  imageTemplateClassName = "",
  imageClassName = "",
  style = {},
  onClick = () => {},
  onDoubleClick = () => {},
  forceSmaller,
  forceSmallerDimension = "width",
  size = 2000,
  artworkData,
  alt,
  enabledDoubleClick = true,
  disableDownload = true,
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [doubleTabLike, setDoubleTabLike] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const privateActionHandler = useProtectAction({ typeOfProtection: "USER_ACCOUNT_REQUIRED" });

  const doubleLike = () => {
    setDoubleTabLike(true);
    setTimeout(() => {
      setDoubleTabLike(false);
    }, 1000);
  };

  const doubleTapFunctions = () => {
    doubleLike();
    onDoubleClick();
  };

  const handleMousePosition = (event) => {
    setPosition({ x: event.clientX, y: event.clientY });
  };

  const manageClick = useDoubleTapHandle(onClick, () => privateActionHandler(doubleTapFunctions), enabledDoubleClick);

  const getAltText = (data) => {
    if (alt) {
      return alt;
    }
    if (data?.artwork_about) {
      const {
        artwork_title: title = "Title",
        artwork_year: year = "",
        artwork_size: { height, length, unit = "cm" } = {},
        artwork_color = [],
        artwork_theme = [],
        artwork_feeling = [],
        artwork_medium: medium
      } = data.artwork_about || {};
      const resolvedArtist = getArtworkArtistDisplayName(data);
      const artist = resolvedArtist && resolvedArtist !== "-" ? resolvedArtist : "Artist";

      const size = `${height}${unit} X ${length}${unit}`;
      const colors = artwork_color.join(", ");
      const emotions = artwork_feeling.join(", ");
      const themes = artwork_theme.join(", ");
      const altText = `${title}, artwork by ${artist}, ${year}. ${size}, ${medium}. ${colors}, ${emotions}, ${themes}. `;
      return altText;
    }
    return "Image in Suarte";
  };

  // Check if image is a blob URL
  const isBlobURL = typeof image === "string" && (image.startsWith("blob:") || image.startsWith("data:image/"));
  const hasImage = typeof image === "string" && image.trim() !== "" && image !== "undefined" && image !== "null";
  const imageUrl = isBlobURL ? image : `${config.app.imageServiceDomain}/${image}/w=${size}`;
  const protectedStyle = disableDownload
    ? { ...style, WebkitTouchCallout: 'none', WebkitUserSelect: 'none', WebkitUserDrag: 'none', userSelect: 'none' }
    : style;

  if (!hasImage) {
    return (
      <div
        role="img"
        aria-label={getAltText(artworkData)}
        className={`artwork-image-template ${imageTemplateClassName} ${imageClassName}`}
        onClick={(event) => {
          manageClick();
          handleMousePosition(event);
        }}
        onContextMenu={disableDownload ? (event) => event.preventDefault() : undefined}
        onDragStart={disableDownload ? (event) => event.preventDefault() : undefined}
        style={protectedStyle}
      />
    );
  }

  return (
    <>
      {!forceSmaller ? (
        <>
          <img
            src={imageUrl}
            srcSet={
              isBlobURL
                ? undefined // Don't use srcSet for blob URLs
                : `${config.app.imageServiceDomain}/${image}/w=200 200w,
                   ${config.app.imageServiceDomain}/${image}/w=400 400w,
                   ${config.app.imageServiceDomain}/${image}/w=600 600w,
                   ${config.app.imageServiceDomain}/${image}/w=800 800w,
                   ${config.app.imageServiceDomain}/${image}/w=1000 1000w,
                   ${config.app.imageServiceDomain}/${image}/w=1200 1200w,
                   ${config.app.imageServiceDomain}/${image}/w=1400 1400w,
                   ${config.app.imageServiceDomain}/${image}/w=1600 1600w,
                   ${config.app.imageServiceDomain}/${image}/w=1800 1800w,
                   ${config.app.imageServiceDomain}/${image}/w=2000 2000w,
                   ${config.app.imageServiceDomain}/${image}/w=2200 2200w`
            }
            loading={forceSmaller ? "lazy" : undefined}
            decoding={forceSmaller ? "async" : undefined}
            alt={getAltText(artworkData)}
            className={!imageLoaded ? "artwork-image-loading" : imageClassName}
            onLoad={() => setImageLoaded(true)}
            onClick={(e) => {
              manageClick();
              handleMousePosition(e);
            }}
            onContextMenu={disableDownload ? (e) => e.preventDefault() : undefined}
            onDragStart={disableDownload ? (e) => e.preventDefault() : undefined}
            draggable={disableDownload ? false : undefined}
            style={protectedStyle}
          />
          <DoubleTabLike position={position} className="artwork-image-double-tap-like__button" open={doubleTabLike} />
        </>
      ) : (
        <img
          src={
            isBlobURL
              ? image // Use blob URL directly
              : forceSmallerDimension === "width"
                ? `${config.app.imageServiceDomain}/${image}/w=${forceSmaller}`
                : `${config.app.imageServiceDomain}/${image}/h=${forceSmaller}`
          }
          loading="lazy"
          decoding="async"
          alt={getAltText(artworkData)}
          className={!imageLoaded ? "artwork-image-loading" : imageClassName}
          onLoad={() => setImageLoaded(true)}
          onClick={manageClick}
          onContextMenu={disableDownload ? (e) => e.preventDefault() : undefined}
          onDragStart={disableDownload ? (e) => e.preventDefault() : undefined}
          draggable={disableDownload ? false : undefined}
          style={protectedStyle}
        />
      )}
      {!imageLoaded && (
        <div className={`artwork-image-template ${imageTemplateClassName}`} style={style} />
      )}
    </>
  );
}
